import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import BigNumber from 'bignumber.js';
import { useInputAmount } from '@/hooks/state/useInputAmount';
import { X } from 'lucide-react';
import { useTradeStore } from '@/page/trade/store';
import { useAppStore } from '@/store';

interface IProps {
  setOpen: (open: boolean) => void;
}

export default (props: IProps) => {
  const { setOpen } = props;
  const { fwClass, chainInfo } = useAppStore();
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const { solTradeSetting, bscTradeSetting, setBscTradeSetting, setSolTradeSetting } = useTradeStore();
  const targetChain = chainInfo.chain == 'solana' ? solTradeSetting : bscTradeSetting;

  // 添加状态管理函数
  const [antiMevOpen, setAntiMevOpen] = useState(targetChain.antiMevOpen);
  const [slippageAuto, setSlippageAuto] = useState(targetChain.slippageAuto);
  const [feeAuto, setFeeAuto] = useState(targetChain.feeAuto);

  const {
    inputAmount: slippage,
    onBlurInputAmount: onBlurInputSlippage,
    onChangeInputAmount: onChangeInputSlippage,
  } = useInputAmount(targetChain.slippage);

  const { inputAmount: fee, onBlurInputAmount: onBlurInputFee, onChangeInputAmount: onChangeInputFee } = useInputAmount(targetChain.fee);

  const isSlippageValid = (slippage: string) => {
    if (slippage == '') return false;
    const slippageObj = BigNumber(slippage);
    return !(slippageObj.lt(0.1) || slippageObj.eq(0) || slippageObj.gt(80));
  };

  const isPriorityFeeValid = (fee: string) => {
    if (fee == '') return false;
    const priorityFeeObj = BigNumber(fee);
    if (chainInfo.chain == 'solana') {
      return !(priorityFeeObj.lt(0.012) || priorityFeeObj.gt(2));
    }
    if (chainInfo.chain == 'bsc') {
      return !(priorityFeeObj.lt(0.1) || priorityFeeObj.gt(20));
    }
  };

  useEffect(() => {
    const shouldDisable = () => {
      if (slippageAuto && feeAuto) {
        return false;
      }
      const slippageCheck = slippageAuto || isSlippageValid(slippage);
      const feeAutoCheck = feeAuto || isPriorityFeeValid(fee);

      return !(slippageCheck && feeAutoCheck);
    };

    setDisabled(shouldDisable());
  }, [slippageAuto, feeAuto, slippage, fee]);

  const SlippageTips = useMemo(() => {
    if (slippageAuto) return () => null;
    const slippageObj = BigNumber(slippage);
    if (slippageObj.eq(0)) return () => <p>{t('Trade.slippageZeroTips')}</p>;
    if (slippageObj.lt(0.1)) return () => <p>{t('Trade.slippageLowTips')}</p>;
    if (slippageObj.gt(80)) return () => <p>{t('Trade.slippageExceedTips')}</p>;
    return () => null;
  }, [slippage, slippageAuto]);

  const FeeTips = useMemo(() => {
    if (feeAuto) return () => null;
    const feeObj = BigNumber(fee);
    if (chainInfo.chain == 'solana') {
      if (feeObj.lt(0.012)) return () => <p>{t('Trade.solPriorityFeeLowTips')}</p>;
      if (feeObj.gt(2)) return () => <p>{t('Trade.solPriorityFeeExceedTips')}</p>;
    }
    if (chainInfo.chain == 'bsc') {
      if (feeObj.lt(0.1)) return () => <p>{t('Trade.bscGasFeeLowTips')}</p>;
      if (feeObj.gt(20)) return () => <p>{t('Trade.bscGasFeeExceedTips')}</p>;
    }
    return () => null;
  }, [fee, feeAuto, chainInfo]);

  const onConfirm = () => {
    const updatedSettings = {
      ...targetChain,
      antiMevOpen,
      slippageAuto,
      feeAuto,
      slippage,
      fee,
    };

    if (chainInfo.chain === 'solana') {
      setSolTradeSetting(updatedSettings);
    } else {
      setBscTradeSetting(updatedSettings);
    }
    setOpen(false);
  };

  return (
    <div className="h-full flex flex-col justify-between">
      <div className=" flex flex-col justify-between gap-y-[20px]">
        <div className="flex justify-between">
          <span className={cn('text-[24px]', fwClass)}>{t('Trade.advancedSetting')}</span>
          <X className="absolute top-[15px] right-[15px] w-[16px] h-[16px]" onClick={() => setOpen(false)} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px]">{t('Trade.AntiMEV')}</span>
          <Checkbox className="size-[20px] rounded-[5px]" checked={antiMevOpen} onCheckedChange={(e: boolean) => setAntiMevOpen(e)} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px]">{t('Trade.slippage')}</span>
          <div className="flex items-center justify-between gap-x-[6px]">
            <Button
              variant="outline"
              className={cn('w-[104px] h-[30px] rounded-[8px]', slippageAuto && '!border-themeColor !border-[1px]')}
              onClick={() => setSlippageAuto(true)}
            >
              Auto
            </Button>
            <div
              className={cn(
                'bg-[#F4F4F5] dark:bg-cardBgColor border-[1px] border-borderColor w-[128px] h-[30px] px-[12px] rounded-[8px] flex items-center',
                !slippageAuto && 'border-themeColor border-[1px]',
              )}
              onClick={() => setSlippageAuto(false)}
            >
              <Input
                className="h-full text-[14px] px-0 shadow-none border-none outline-none"
                placeholder="Custom"
                inputMode="decimal"
                value={slippage}
                onChange={onChangeInputSlippage}
                onBlur={onBlurInputSlippage}
              />
              <span>%</span>
            </div>
          </div>
        </div>
        <div className="text-[#F0B90B] text-[12px]">
          <SlippageTips />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px]">{chainInfo.chain == 'solana' ? t('Trade.priorityFee') : t('Trade.gasFee')}</span>
          <div className="flex items-center justify-between gap-x-[6px]">
            <Button
              variant="outline"
              className={cn('w-[104px] h-[30px] rounded-[8px]', feeAuto && '!border-themeColor !border-[1px]')}
              onClick={() => setFeeAuto(true)}
            >
              Auto
            </Button>
            <div
              className={cn(
                'bg-[#F4F4F5] dark:bg-cardBgColor border-[1px] border-borderColor w-[128px] h-[30px] px-[12px] rounded-[8px] flex items-center',
                !feeAuto && '!border-themeColor !border-[1px]',
              )}
              onClick={() => setFeeAuto(false)}
            >
              <Input
                className="h-full border-none outline-none text-[14px] px-0 shadow-none"
                placeholder="Custom"
                inputMode="decimal"
                value={fee}
                onChange={onChangeInputFee}
                onBlur={onBlurInputFee}
              />
              <span>{chainInfo.chain == 'solana' ? chainInfo.symbol : 'Gwei'}</span>
            </div>
          </div>
        </div>
        <div className="text-[#F0B90B] text-[12px]">
          <FeeTips />
        </div>
        <div />
      </div>
      <Button className="w-full !bg-themeColor rounded-[12px] h-[48px] text-primaryTextColor flex-shrink-0" disabled={disabled} onClick={onConfirm}>
        <span className={fwClass}>{t('Trade.confirm')}</span>
      </Button>
    </div>
  );
};
